/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useLanguage } from '@/providers/LanguageProvider';
import { AppListItem } from 'types/app-list';
import { FilterType, MeasuresData } from 'types/measures-api-data';

import { NotFoundMessage } from '../not-found';
import { AppCards } from './app-cards';

interface AppCardsListProps {
  apps: AppListItem[];
  locale: string;
  measuresData: MeasuresData;
  activeFilter: FilterType;
  loading: boolean;
}

export const AppCardsList: React.FC<AppCardsListProps> = ({
  apps,
  locale,
  measuresData,
  activeFilter,
  loading,
}) => {
  const { messages } = useLanguage();
  const [savedPins, setSavedPins] = useState<string[]>([]);

  useEffect(() => {
    setSavedPins(JSON.parse(localStorage.getItem('pinnedApps') || '[]'));
  }, []);

  const handlePinToggle = (appName: string) => {
    const updatedPins = savedPins.includes(appName)
      ? savedPins.filter((name) => name !== appName)
      : [...savedPins, appName];
    localStorage.setItem('pinnedApps', JSON.stringify(updatedPins));
    setSavedPins(updatedPins);
  };

  const sortedApps = [...apps].sort((a, b) => {
    const isPinnedA = savedPins.includes(a.name);
    const isPinnedB = savedPins.includes(b.name);
    return Number(isPinnedB) - Number(isPinnedA);
  });

  // Merged apps with error and sla count
  const mergedApps = sortedApps.map((app) => {
    const countData = measuresData[app.name];

    if (countData)
      return {
        ...app,
        ...countData,
      };

    return app;
  });

  // Final filtering based on activeFilter (this is the key filtering logic)
  const finalFilteredApps = mergedApps.filter((app) => {
    // show all apps
    if (activeFilter === 'all') return true;

    // Check if app has measures data
    const appMeasures = measuresData[app.name];
    if (!appMeasures) return false;

    // Filter by error count
    if (activeFilter === 'error') {
      return appMeasures.errors > 0;
    }

    // Filter by performance/SLA count
    if (activeFilter === 'performance') {
      return appMeasures.not_meet_sla > 0;
    }

    // Filter by budget/token count
    if (activeFilter === 'budget') {
      return appMeasures.not_meet_budget_sla > 0;
    }

    //Filter by test failures
    if (activeFilter === 'test_failures') {
      return appMeasures.test_failures > 0;
    }

    return true;
  });

  return finalFilteredApps.length > 0 ? (
    finalFilteredApps.map((app: AppListItem) => (
      <Link
        id={app.is_playground ? 'step-1' : app.name}
        href={`/${locale}/apps/${app.name}${app.is_playground ? '?days=all' : ''}`}
        key={app.name}
        className="h-full"
      >
        <AppCards
          app={app}
          loading={loading}
          onPinToggle={handlePinToggle}
          isPinned={savedPins.includes(app.name)}
        />
      </Link>
    ))
  ) : (
    <div className="col-span-full">
      <NotFoundMessage
        title={messages?.DashboardPage?.no_applications_found}
        message={messages?.DashboardPage?.no_app_registered_msg}
      />
    </div>
  );
};
